import { DATE, DATE_RANGE, DROPDOWN, INTERACTION_AUDIT_STATUSES, INTERACTION_MODES, RANGE, TEXT, NUMBER } from "../constants/constants";
import { INTERACTION_TYPES } from "../constants/interactionTypes";
import Agency from "../interfaces/agency";
import Client from "../interfaces/client";
import Filter from "../interfaces/filters";
import Picklist from "../interfaces/picklist";
import Segment from "../interfaces/segment";
import User, { EmploymentType, UserStatus } from "../interfaces/user";
import { currentPoolMonth, generateCurrentDate, isObject } from "./commonUtils";

export const createFilters = (
    filterKeys: string[], segments: Segment[], clients: Client[], agencies: Agency[], picklist: Picklist, states: string[], districts: string[],
    users: User[], clientManagerList: string[], multiSelect = true
): Filter[] => {
    return filterKeys.map(val => {
        switch (val) {
            case 'client_name':
                return {
                    id: val,
                    key: val,
                    value: [],
                    label: "Select Client",
                    disabled: false,
                    type: DROPDOWN,
                    list: clients,
                    multiple: multiSelect,
                    keyProp: "client_name",
                    valueProp: "client_name",
                    displayProp: "name",
                    filterOptions: false,
                };
            case 'agency_name':
                return {
                    id: val,
                    key: val,
                    value: [],
                    label: "Select Agency",
                    disabled: false,
                    type: DROPDOWN,
                    list: agencies,
                    multiple: multiSelect,
                    keyProp: "agency_name",
                    valueProp: "agency_name",
                    displayProp: "name",
                    filterOptions: false,
                };
            case 'status':
                return {
                    id: val,
                    key: val,
                    value: [],
                    label: "Select Status",
                    disabled: false,
                    type: DROPDOWN,
                    list: picklist.STATUS,
                    multiple: multiSelect,
                    filterOptions: false,
                };
            case 'loan_type':
                return {
                    id: val,
                    key: val,
                    value: [],
                    label: "Select Loan Type",
                    disabled: false,
                    type: DROPDOWN,
                    list: picklist.LOAN_TYPE,
                    multiple: multiSelect,
                    filterOptions: false,
                };
            case 'dpd_bucket':
                return {
                    id: val,
                    key: val,
                    value: [],
                    label: "Select DPD Bucket",
                    disabled: false,
                    type: DROPDOWN,
                    list: picklist.DPD_BUCKET,
                    multiple: multiSelect,
                    filterOptions: false,
                };
            case 'segment_id':
                return {
                    id: val,
                    key: val,
                    value: [],
                    label: "Select Segment",
                    disabled: false,
                    type: DROPDOWN,
                    list: segments,
                    multiple: multiSelect,
                    keyProp: "segment_id",
                    valueProp: "segment_id",
                    displayProp: "name",
                    filterOptions: false,
                };
            case 'state':
                return {
                    id: val,
                    key: val,
                    value: [],
                    label: "Select State",
                    disabled: false,
                    type: DROPDOWN,
                    list: states,
                    multiple: multiSelect,
                    filterOptions: true,
                    noOptionsText: "Search state"
                };
            case 'district':
                return {
                    id: val,
                    key: val,
                    value: [],
                    label: "Select District",
                    disabled: false,
                    type: DROPDOWN,
                    list: districts,
                    multiple: multiSelect,
                    filterOptions: true,
                    noOptionsText: "Search district"
                };
            case 'allocated_to':
                return {
                    id: val,
                    key: val,
                    value: [],
                    label: "Select Allocated To",
                    disabled: false,
                    type: DROPDOWN,
                    list: users,
                    multiple: multiSelect,
                    keyProp: "username",
                    valueProp: "username",
                    displayProp: "username",
                    filterOptions: false,
                };
            case 'user_lookup':
                return {
                    id: val,
                    key: 'username',
                    value: '',
                    label: "Select Username",
                    disabled: false,
                    type: DROPDOWN,
                    list: users,
                    multiple: false,
                    keyProp: "username",
                    valueProp: "username",
                    displayProp: "username",
                    filterOptions: false,
                    noOptionsText: "Search username",
                };
            case 'active_allocation':
                return {
                    id: val,
                    key: val,
                    value: [],
                    label: "Is Active Allocation",
                    disabled: false,
                    type: DROPDOWN,
                    list: ['true', 'false'],
                    multiple: false,
                    filterOptions: false,
                };
            case 'client_manager_username':
                return {
                    id: val,
                    key: val,
                    value: [],
                    label: "Client Manager",
                    disabled: false,
                    type: DROPDOWN,
                    list: clientManagerList,
                    multiple: multiSelect,
                    filterOptions: false,
                };
            case 'allocation_expiry_date_ge':
                return {
                    id: val,
                    key: val,
                    value: "",
                    label: "Select Expiry Date",
                    type: DATE,
                    disabled: false,
                    filterOptions: false,
                    multiple: false
                };
            case 'principal_outstanding_amt':
                return {
                    id: val,
                    key: val,
                    value: { min: '', max: '' },
                    label: "Principal Outstanding Amount",
                    type: RANGE,
                    disabled: false,
                    filterOptions: false,
                    multiple: false,
                    rangeKeys: {
                        startKey: { key: 'principal_outstanding_amt_min', valueKey: 'min' },
                        endKey: { key: 'principal_outstanding_amt_max', valueKey: 'max' },
                    }
                };
            case 'total_due_amt':
                return {
                    id: val,
                    key: val,
                    value: { min: '', max: '' },
                    label: "Total Overdue Amount",
                    type: RANGE,
                    disabled: false,
                    filterOptions: false,
                    multiple: false,
                    rangeKeys: {
                        startKey: { key: 'total_due_amt_min', valueKey: 'min' },
                        endKey: { key: 'total_due_amt_max', valueKey: 'max' },
                    }
                };
            case 'focused':
                return {
                    id: val,
                    key: val,
                    value: "",
                    label: "Focused Case",
                    type: DROPDOWN,
                    list: [ "true", "false"],
                    disabled: false,
                    filterOptions: false,
                    multiple: false
                };
            case 'interaction_type':
                return {
                    id: val,
                    key: val,
                    value: [],
                    label: "Interaction Type",
                    type: DROPDOWN,
                    list: INTERACTION_TYPES,
                    disabled: false,
                    filterOptions: false,
                    multiple: true
                };
            case 'interaction_mode':
                return {
                    id: val,
                    key: val,
                    value: [],
                    label: "Interaction Mode",
                    type: DROPDOWN,
                    list: INTERACTION_MODES,
                    disabled: false,
                    filterOptions: false,
                    multiple: true
                };
            case 'interaction_audit_status':
                return {
                    id: val,
                    key: 'status',
                    value: [INTERACTION_AUDIT_STATUSES.pending],
                    label: "Status",
                    type: DROPDOWN,
                    list: Object.values(INTERACTION_AUDIT_STATUSES),
                    disabled: false,
                    filterOptions: false,
                    multiple: true
                }
            case 'interaction_by':
                return {
                    id: val,
                    key: val,
                    value: [],
                    label: "Interaction By",
                    disabled: false,
                    type: DROPDOWN,
                    list: users,
                    multiple: multiSelect,
                    keyProp: "username",
                    valueProp: "username",
                    displayProp: "username",
                    filterOptions: false,
                };
            case 'interaction_date':
                return {
                    id: val,
                    key: val,
                    value: "",
                    label: "Interaction Date",
                    type: DATE,
                    disabled: false,
                    filterOptions: false,
                    multiple: false
                };
            case 'Distance':
                return {
                    id: val,
                    key: val,
                    value: { min: '', max: '' },
                    label: "Distance",
                    type: RANGE,
                    disabled: false,
                    filterOptions: false,
                    multiple: false,
                    rangeKeys: {
                        startKey: { key: 'distance_min', valueKey: 'min' },
                        endKey: { key: 'distance_max', valueKey: 'max' }
                    }
                };
            case 'IsBlur':
                return {
                    id: val,
                    key: val,
                    value: '',
                    label: "Is Blur",
                    type: DROPDOWN,
                    disabled: false,
                    filterOptions: false,
                    multiple: false,
                    list: ['true', 'false']
                };
            case 'campaign_date':
                return {
                    id: val,
                    key: val,
                    value: { startDate: currentPoolMonth(), endDate: generateCurrentDate() },
                    label: "Campaign Date Range",
                    type: DATE_RANGE,
                    disabled: false,
                    filterOptions: false,
                    multiple: false,
                    rangeKeys: {
                        startKey: { key: 'start_date', valueKey: 'startDate' },
                        endKey: { key: 'end_date', valueKey: 'endDate' },
                    }
                };
            case 'phone_number':
                return {
                    id: val,
                    key: val,
                    value: "",
                    label: "Phone Number",
                    type: NUMBER,
                    disabled: false,
                    filterOptions: false,
                    multiple: false,
                };
            case 'registration_request_date':
                return {
                    id: val,
                    key: val,
                    value: { startDate: '', endDate: '' },
                    label: "Registration Date Range",
                    type: DATE_RANGE,
                    disabled: false,
                    filterOptions: false,
                    multiple: false,
                    rangeKeys: {
                        startKey: { key: 'start_date', valueKey: 'startDate' },
                        endKey: { key: 'end_date', valueKey: 'endDate' },
                    }
                };
            case 'user_status':
                return {
                    id: val,
                    key: val,
                    value: [UserStatus.APPROVED],
                    label: "User Status",
                    disabled: false,
                    type: DROPDOWN,
                    list: Object.values(UserStatus),
                    multiple: true,
                    filterOptions: false,
                };
            case 'employment_type':
                return {
                    id: val,
                    key: val,
                    value: [],
                    label: "Employment Type",
                    disabled: false,
                    type: DROPDOWN,
                    list: Object.values(EmploymentType),
                    multiple: false,
                    filterOptions: false,
                };
            default:
                return {
                    id: val,
                    key: val,
                    value: "",
                    label: val.replaceAll('_', ' '),
                    type: TEXT,
                    disabled: false,
                    filterOptions: false,
                    multiple: false
                };
        }
    });
};

export const getSelectedFiltersCount = (filters: Filter[]) => {
    const filterLength = filters.filter(filter => {
        if (Array.isArray(filter.value) || typeof filter.value === 'string') {
            return filter.value.length > 0;
        } else if (isObject(filter.value)) {
            return Object.entries(filter.value).filter(val => val[1]).length > 0;
        }
    }).length;
    return filterLength;
};